import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonFab,
    IonFabButton,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonLoading,
    IonModal,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToast,
    IonToolbar,
    isPlatform,
    useIonAlert,
    useIonViewDidEnter
} from '@ionic/react';
import { add, arrowBack, createOutline, trashOutline } from 'ionicons/icons';
import React, { useContext, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MessageContext } from '../../../../../contexts/MessageContext';
import {
    fiscalAtualizarCategoria,
    fiscalCriarCategoria,
    fiscalDesativarCategoria,
    fiscalListarCategorias,
    fiscalSeedCategorias,
    requestService
} from '../../../../../Utils/Services';
import './FiscalCategorias.css';

type Props = {
    abrirMenu: boolean;
};

const FiscalCategorias: React.FC<Props> = (props: Props) => {
    const history = useHistory();
    const { loja } = useContext(MessageContext);
    const { esconderMenu, setEsconderMenu } = useContext(MessageContext);
    const { nomeMenu, setNomeMenu } = useContext(MessageContext);

    const [categorias, setCategorias] = useState<any[]>([]);
    const [showLoading, setShowLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [textToast, setTextToast] = useState("");
    const [toastColor, setToastColor] = useState("success");

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editando, setEditando] = useState(false);
    const [categoriaEditId, setCategoriaEditId] = useState("");

    // Form fields
    const [nome, setNome] = useState("");
    const [ncm, setNcm] = useState("");
    const [cfop, setCfop] = useState("");
    const [csosn, setCsosn] = useState("400");
    const [origem, setOrigem] = useState("0");

    const [acessos] = useState<any>(JSON.parse(window.localStorage.getItem("acessos") || '{}'));
    const isWeb = isPlatform('desktop');

    const [mostrarAlerta] = useIonAlert();
    const modal = useRef<HTMLIonModalElement>(null);

    useIonViewDidEnter(() => {
        if (acessos?.configuracoes != 'leitura' && acessos?.configuracoes != 'edicao') {
            history.replace('/empresa/home');
            return;
        }
        setEsconderMenu(true);
        setNomeMenu("");
        carregarCategorias();
    });

    const carregarCategorias = () => {
        if (!loja) return;
        setShowLoading(true);

        const url = fiscalListarCategorias.url.replace('{empresaId}', encodeURIComponent(loja));
        requestService(url, { method: fiscalListarCategorias.method }, history,
            (response: any) => {
                setShowLoading(false);
                if (response.dados && Array.isArray(response.dados)) {
                    setCategorias(response.dados);
                    // Auto-seed: se não há categorias, inserir padrões silenciosamente
                    if (response.dados.length === 0) {
                        requestService(
                            fiscalSeedCategorias.url,
                            {
                                method: fiscalSeedCategorias.method,
                                body: JSON.stringify({ empresaId: loja, segmento: 'ALIMENTACAO' }),
                                credentials: 'include'
                            },
                            history,
                            (_seedRes: any) => {
                                // Recarregar lista após seed
                                const urlReload = fiscalListarCategorias.url.replace('{empresaId}', encodeURIComponent(loja));
                                requestService(urlReload, { method: fiscalListarCategorias.method }, history,
                                    (res2: any) => {
                                        if (res2.dados && Array.isArray(res2.dados)) {
                                            setCategorias(res2.dados);
                                        }
                                    },
                                    () => { /* silent */ }
                                );
                            },
                            () => { /* seed falhou silenciosamente */ }
                        );
                    }
                } else {
                    setCategorias([]);
                }
            },
            (error: any) => {
                setShowLoading(false);
                setTextToast("Erro ao carregar categorias fiscais");
                setToastColor("danger");
                setShowToast(true);
                console.error(error);
            });
    };

    const limparForm = () => {
        setNome("");
        setNcm("");
        setCfop("");
        setCsosn("400");
        setOrigem("0");
        setCategoriaEditId("");
        setEditando(false);
    };

    const abrirModalNovo = () => {
        limparForm();
        setShowModal(true);
    };

    const abrirModalEditar = (cat: any) => {
        setNome(cat.nome || "");
        setNcm(cat.ncm || "");
        setCfop(cat.cfop || "");
        setCsosn(cat.csosn || "400");
        setOrigem(cat.origem || "0");
        setCategoriaEditId(cat.id || cat.sk?.replace('categoriaFiscal#', ''));
        setEditando(true);
        setShowModal(true);
    };

    const validarForm = (): boolean => {
        if (!nome.trim()) {
            setTextToast("Informe o nome da categoria");
            setToastColor("warning");
            setShowToast(true);
            return false;
        }
        if (!ncm || ncm.length !== 8) {
            setTextToast("NCM deve ter exatamente 8 dígitos");
            setToastColor("warning");
            setShowToast(true);
            return false;
        }
        if (!cfop || cfop.length !== 4) {
            setTextToast("CFOP deve ter exatamente 4 dígitos");
            setToastColor("warning");
            setShowToast(true);
            return false;
        }
        return true;
    };

    const salvarCategoria = () => {
        if (!validarForm()) return;

        setShowLoading(true);
        const payload = {
            empresaId: loja,
            nome: nome.trim(),
            ncm,
            cfop,
            csosn,
            origem
        };

        if (editando && categoriaEditId) {
            // Atualizar
            const url = fiscalAtualizarCategoria.url.replace('{id}', encodeURIComponent(categoriaEditId));
            requestService(url, {
                method: fiscalAtualizarCategoria.method,
                body: JSON.stringify(payload),
                headers: [['Content-Type', 'application/json']]
            }, history,
                (response: any) => {
                    setShowLoading(false);
                    setShowModal(false);
                    setTextToast("Categoria atualizada com sucesso");
                    setToastColor("success");
                    setShowToast(true);
                    carregarCategorias();
                },
                (error: any) => {
                    setShowLoading(false);
                    setTextToast(error?.message || "Erro ao atualizar categoria");
                    setToastColor("danger");
                    setShowToast(true);
                });
        } else {
            // Criar
            requestService(fiscalCriarCategoria.url, {
                method: fiscalCriarCategoria.method,
                body: JSON.stringify(payload),
                headers: [['Content-Type', 'application/json']]
            }, history,
                (response: any) => {
                    setShowLoading(false);
                    setShowModal(false);
                    setTextToast("Categoria criada com sucesso");
                    setToastColor("success");
                    setShowToast(true);
                    carregarCategorias();
                },
                (error: any) => {
                    setShowLoading(false);
                    setTextToast(error?.message || "Erro ao criar categoria");
                    setToastColor("danger");
                    setShowToast(true);
                });
        }
    };

    // Categorias de taxa usadas internamente pelo fiscal adapter — não podem ser excluídas
    const isCategoriaProtegida = (cat: any): boolean => {
        const nome = (cat.nome || '').toLowerCase();
        return nome.includes('servi') || nome.includes('entreg') || nome.includes('frete');
    };

    const confirmarExclusao = (cat: any) => {
        const catId = cat.id || cat.sk?.replace('categoriaFiscal#', '');
        mostrarAlerta({
            header: 'Desativar Categoria',
            message: `Deseja desativar a categoria "${cat.nome}"?`,
            buttons: [
                { text: 'Cancelar', role: 'cancel' },
                {
                    text: 'Desativar', handler: () => {
                        setShowLoading(true);
                        const url = fiscalDesativarCategoria.url
                            .replace('{id}', encodeURIComponent(catId))
                            .replace('{empresaId}', encodeURIComponent(loja));
                        requestService(url, { method: fiscalDesativarCategoria.method }, history,
                            (response: any) => {
                                setShowLoading(false);
                                setTextToast("Categoria desativada");
                                setToastColor("success");
                                setShowToast(true);
                                carregarCategorias();
                            },
                            (error: any) => {
                                setShowLoading(false);
                                setTextToast("Erro ao desativar categoria");
                                setToastColor("danger");
                                setShowToast(true);
                            });
                    }
                }
            ]
        });
    };

    return (
        <IonPage>
            <IonHeader className="ion-no-border">
                <IonToolbar color="primary">
                    <IonButtons slot="start">
                        <IonBackButton icon={arrowBack} text='' />
                    </IonButtons>
                    <IonTitle slot="start">Categorias Fiscais</IonTitle>
                </IonToolbar>
                {isWeb && (
                    <IonToolbar hidden={acessos?.configuracoes != 'edicao'}>
                        <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 8px 0 8px" }}>
                            <IonButton
                                fill="solid"
                                size="large"
                                color="tertiary"
                                onClick={() => {
                                    abrirModalNovo();
                                }}
                            >
                                <IonIcon icon={add} slot="start" />
                                Nova categoria
                            </IonButton>
                        </div>
                    </IonToolbar>
                )}
            </IonHeader>
            <IonContent className="fiscal-categorias-container">
                <IonLoading isOpen={showLoading} message="Carregando..." />

                {categorias.length === 0 && !showLoading && (
                    <div style={{ textAlign: 'center', marginTop: '40px', color: 'var(--ion-color-medium)' }}>
                        <p>Nenhuma categoria fiscal cadastrada.</p>
                        <p style={{ fontSize: '0.85rem' }}>Use o botão + para criar sua primeira categoria.</p>
                    </div>
                )}

                {categorias.map((cat: any, idx: number) => (
                    <IonCard key={cat.id || idx}>
                        <IonCardHeader>
                            <IonCardTitle style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>
                                    {cat.nome}
                                    {/* <span className={cat.ativo !== false ? 'badge-ativo' : 'badge-inativo'}>
                                        {cat.ativo !== false ? 'Ativo' : 'Inativo'}
                                    </span> */}
                                </span>
                                <span>
                                    <IonButton disabled={acessos?.configuracoes != 'edicao'} fill="clear" size="small" onClick={() => abrirModalEditar(cat)}>
                                        <IonIcon icon={createOutline} />
                                    </IonButton>
                                    {!isCategoriaProtegida(cat) && (
                                        <IonButton disabled={acessos?.configuracoes != 'edicao'} fill="clear" size="small" color="danger" onClick={() => confirmarExclusao(cat)}>
                                            <IonIcon icon={trashOutline} />
                                        </IonButton>
                                    )}
                                </span>
                            </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <div className="info-row">
                                <div className="info-item">
                                    <span>NCM</span>
                                    <span>{cat.ncm}</span>
                                </div>
                                <div className="info-item">
                                    <span>CFOP</span>
                                    <span>{cat.cfop}</span>
                                </div>
                                <div className="info-item">
                                    <span>CSOSN</span>
                                    <span>{cat.csosn || '400'}</span>
                                </div>
                                <div className="info-item">
                                    <span>Origem</span>
                                    <span>{cat.origem || '0'}</span>
                                </div>
                            </div>
                        </IonCardContent>
                    </IonCard>
                ))}

                {!isWeb && (
                    <IonFab vertical="bottom" horizontal="center" slot="fixed" hidden={acessos?.configuracoes != 'edicao'}>
                        <IonFabButton disabled={acessos?.configuracoes != 'edicao'} color="tertiary" class="custom-fab-button" onClick={abrirModalNovo}>
                            <IonIcon color="light" icon={add}></IonIcon>
                        </IonFabButton>
                    </IonFab>
                )}

                {/* Modal de criação/edição */}
                <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)} ref={modal}>
                    <IonHeader className="ion-no-border">
                        <IonToolbar>
                            <IonButtons slot="start">
                                <IonButton onClick={() => { setShowModal(false); }}>
                                    <IonIcon slot="icon-only" icon={arrowBack} />
                                </IonButton>
                            </IonButtons>
                            <IonTitle>{editando ? 'Editar Categoria' : 'Nova Categoria'}</IonTitle>
                            <IonButtons slot="end">
                                <IonButton strong onClick={salvarCategoria}>Salvar</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent className="ion-padding">
                        <IonList>
                            <IonItem>
                                <IonLabel position="stacked">Nome *</IonLabel>
                                <IonInput
                                    value={nome}
                                    placeholder="Ex: Alimentos, Bebidas, etc."
                                    onIonChange={(e: any) => setNome(e.detail.value || "")}
                                />
                            </IonItem>

                            <IonItem>
                                <IonLabel position="stacked">NCM * (8 dígitos)</IonLabel>
                                <IonInput
                                    value={ncm}
                                    placeholder="Ex: 21069090"
                                    maxlength={8}
                                    type="number"
                                    onIonChange={(e: any) => setNcm(e.detail.value || "")}
                                />
                            </IonItem>

                            <IonItem>
                                <IonLabel position="stacked">CFOP * (4 dígitos)</IonLabel>
                                <IonInput
                                    value={cfop}
                                    placeholder="Ex: 5102"
                                    maxlength={4}
                                    type="number"
                                    onIonChange={(e: any) => setCfop(e.detail.value || "")}
                                />
                            </IonItem>

                            <IonItem>
                                <IonLabel position="stacked">CSOSN</IonLabel>
                                <IonSelect value={csosn} onIonChange={(e: any) => setCsosn(e.detail.value)}>
                                    <IonSelectOption value="102">102 - Tributação SN sem permissão de crédito</IonSelectOption>
                                    <IonSelectOption value="103">103 - Isenção do ICMS para faixa de receita bruta</IonSelectOption>
                                    <IonSelectOption value="300">300 - Imune</IonSelectOption>
                                    <IonSelectOption value="400">400 - Não tributado pelo SN</IonSelectOption>
                                    <IonSelectOption value="500">500 - ICMS cobrado anteriormente por ST</IonSelectOption>
                                    <IonSelectOption value="900">900 - Outros</IonSelectOption>
                                </IonSelect>
                            </IonItem>

                            <IonItem>
                                <IonLabel position="stacked">Origem da Mercadoria</IonLabel>
                                <IonSelect value={origem} onIonChange={(e: any) => setOrigem(e.detail.value)}>
                                    <IonSelectOption value="0">0 - Nacional</IonSelectOption>
                                    <IonSelectOption value="1">1 - Estrangeira (importação direta)</IonSelectOption>
                                    <IonSelectOption value="2">2 - Estrangeira (adquirida no mercado interno)</IonSelectOption>
                                    <IonSelectOption value="3">3 - Nacional (conteúdo importação 40-70%)</IonSelectOption>
                                    <IonSelectOption value="5">5 - Nacional (conteúdo importação até 40%)</IonSelectOption>
                                    <IonSelectOption value="8">8 - Nacional (conteúdo importação acima 70%)</IonSelectOption>
                                </IonSelect>
                            </IonItem>

                        </IonList>
                    </IonContent>
                </IonModal>

                <IonToast
                    isOpen={showToast}
                    position="middle"
                    onDidDismiss={() => setShowToast(false)}
                    message={textToast}
                    color={toastColor}
                    duration={3000}
                />
            </IonContent>
        </IonPage>
    );
};

export default FiscalCategorias;
